import { useCallback, useEffect, useState } from "react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminShopCatalog, getAdminShopCatalog } from "../../../api/shop";
import { useAppDispatch } from "../../../store/store";
import { setGeneralAlertMessage } from "../../../store/slices/generalSlice";
import { Button, Card, EmptyState, Loader, PageHeader, Tabs } from "../../../ui";
import type { CatalogTab, CatalogTabProps } from "./types";
import SizesTab from "./SizesTab";
import VariantsTab from "./VariantsTab";
import CategoriesTab from "./CategoriesTab";
import SportsTab from "./SportsTab";
import "../styles.scss";
import "../shared/styles.scss";
import "./styles.scss";

type TabView = {
  value: CatalogTab;
  label: string;
  count: (catalog: AdminShopCatalog) => number;
  View: ComponentType<CatalogTabProps>;
};

// One tab per part of the catalog, in the order staff set them up
const TAB_VIEWS: TabView[] = [
  { value: "sizes", label: "Sizes", count: (c) => c.sizes.length, View: SizesTab },
  { value: "variants", label: "Variants", count: (c) => c.optionTypes.length, View: VariantsTab },
  { value: "categories", label: "Categories", count: (c) => c.categories.length, View: CategoriesTab },
  { value: "sports", label: "Sports", count: (c) => c.sports.filter((s) => s.isSetUp !== false).length, View: SportsTab },
];

const ShopCatalogSetup = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = TAB_VIEWS.find((t) => t.value === searchParams.get("tab")) ?? TAB_VIEWS[0];
  const [catalog, setCatalog] = useState<AdminShopCatalog | null>(null);
  const [loading, setLoading] = useState(true);

  const showError = useCallback(
    (err: any, fallback: string) => dispatch(setGeneralAlertMessage({ type: "error", message: err?.message || fallback })),
    [dispatch]
  );
  const showSuccess = useCallback(
    (message: string) => dispatch(setGeneralAlertMessage({ type: "success", message })),
    [dispatch]
  );

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminShopCatalog();
      setCatalog(res?.data ?? null);
    } catch (err: any) {
      console.error("Error fetching the shop catalog", err);
      showError(err, "Failed to load the catalog");
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    document.title = "Shop Catalog Setup";
    fetchCatalog();
  }, [fetchCatalog]);

  // The tab lives in ?tab= so a refresh or a shared link opens the same one
  const setTab = (tab: CatalogTab) =>
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", tab);
        return p;
      },
      { replace: true }
    );

  const View = active.View;
  return (
    <div className="shop-page shop-catalog-page">
      <PageHeader
        title="Catalog setup"
        subtitle="Sizes, variants, categories and sports: set them up first, then build products from them"
      >
        <Tabs
          ariaLabel="Catalog setup"
          value={active.value}
          onChange={setTab}
          options={TAB_VIEWS.map((t) => ({ value: t.value, label: t.label, count: catalog ? t.count(catalog) : undefined }))}
        />
      </PageHeader>

      {loading && !catalog ? (
        <Loader />
      ) : !catalog ? (
        <Card padding="none">
          <EmptyState title="The catalog couldn't be loaded." action={<Button onClick={fetchCatalog}>Try again</Button>} />
        </Card>
      ) : (
        <View catalog={catalog} onChanged={fetchCatalog} showError={showError} showSuccess={showSuccess} />
      )}
    </div>
  );
};

export default ShopCatalogSetup;
